import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { X } from "@phosphor-icons/react";

/**
 * Reusable CRUD modal.
 */
export default function CrudModal({ open, onClose, title, fields, initial = {}, onSubmit, submitLabel }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setValues(initial || {}); /* reset on open */ }, [open, initial]);

  if (!open) return null;

  const handleChange = (name, v) => setValues(prev => ({ ...prev, [name]: v }));

  const submit = async () => {
    setBusy(true);
    try { await onSubmit(values); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="crud-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white rounded-md border border-border w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-bold text-xl tracking-tight">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="crud-close-btn"><X size={16}/></button>
        </div>

        <div className="mt-5 space-y-3">
          {fields.map(f => (
            <div key={f.name}>
              <label className="eos-overline">{f.label}</label>
              {f.type === "select" ? (
                <select
                  value={values[f.name] ?? ""}
                  onChange={e => handleChange(f.name, e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-white focus:outline-none focus:border-foreground/40"
                  data-testid={`crud-input-${f.name}`}
                >
                  <option value="">—</option>
                  {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === "switch" ? (
                <button
                  type="button"
                  onClick={() => handleChange(f.name, !values[f.name])}
                  className={`mt-1 inline-flex w-12 h-6 rounded-full transition-colors ${values[f.name] ? "bg-[hsl(244_53%_20%)]" : "bg-border"}`}
                  data-testid={`crud-input-${f.name}`}
                >
                  <span className={`w-5 h-5 rounded-full bg-white transition-transform mt-0.5 ${values[f.name] ? "translate-x-6" : "translate-x-0.5"}`}/>
                </button>
              ) : f.type === "textarea" ? (
                <textarea
                  value={values[f.name] ?? ""}
                  onChange={e => handleChange(f.name, e.target.value)}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:border-foreground/40"
                  data-testid={`crud-input-${f.name}`}
                />
              ) : (
                <input
                  type={f.type === "number" ? "number" : "text"}
                  value={values[f.name] ?? ""}
                  onChange={e => handleChange(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:border-foreground/40"
                  data-testid={`crud-input-${f.name}`}
                />
              )}
              {f.hint && <div className="text-[11px] text-muted-foreground mt-1">{f.hint}</div>}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-md" data-testid="crud-cancel-btn">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="crud-submit-btn">
            {busy ? t("common.saving") : (submitLabel || t("common.save"))}
          </Button>
        </div>
      </div>
    </div>
  );
}
